// src/pages/publish/components/CourseTree.tsx
import React from 'react';
import { Layout, Layers, MonitorPlay, FileText, UploadCloud, Trash2 } from 'lucide-react';
import { TreeNode } from './TreeNode';
import type { PublishCourse } from '../../../lib/api';

interface CourseTreeProps {
  courses: PublishCourse[];
  isInternal: boolean;
  // 增加 'plan' 类型定义
  onPublish: (type: 'course' | 'stage' | 'lesson' | 'plan' | 'slide', id: number, extra?: any) => void;
  onDelete: (type: 'course' | 'stage' | 'lesson' | 'plan' | 'slide', id: number) => void;
}

export const CourseTree: React.FC<CourseTreeProps> = ({ 
  courses, 
  isInternal, 
  onPublish, 
  onDelete 
}) => {
  if (!courses || courses.length === 0) {
    return <div className="text-gray-400 text-sm p-4 text-center">暂无数据</div>;
  }

  // 排序幻灯片
  const sortSlides = (slides: any[]) => {
    return [...(slides || [])].sort((a, b) => a.slide_index - b.slide_index);
  };

  // 生成操作按钮
  const renderActions = (type: 'course' | 'stage' | 'lesson' | 'plan' | 'slide', id: number, extra?: any) => {
    return (
      <>
        {isInternal && (
          <button 
            onClick={(e) => { e.stopPropagation(); onPublish(type, id, extra); }}
            className="p-1 hover:bg-blue-100 text-blue-600 rounded transition-colors" 
            title={`发布 ${type}`}
          >
            <UploadCloud size={16} />
          </button>
        )}
        {!isInternal && (
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(type, id); }}
            className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors" 
            title={`删除线上 ${type}`}
          >
            <Trash2 size={16} />
          </button>
        )}
      </>
    );
  };

  return (
    <>
      {courses.map(course => (
        <TreeNode
          key={`c-${course.id}`}
          level={0}
          label={<span className="font-bold text-lg">{course.title}</span>}
          icon={Layout}
          defaultExpanded={true}
          actions={renderActions('course', course.id)}
        >
          {course.stages?.map(stage => (
            <TreeNode
              key={`s-${stage.id}`}
              level={1}
              label={stage.title}
              icon={Layers}
              actions={renderActions('stage', stage.id)}
            >
              {stage.lessons?.map(lesson => (
                <TreeNode
                  key={`l-${lesson.id}`}
                  level={2}
                  label={
                    <>
                      <span className="text-xs text-gray-400 mr-1">L{lesson.sort_order ?? lesson.id}</span>
                      {lesson.title}
                    </>
                  }
                  icon={MonitorPlay}
                  actions={renderActions('lesson', lesson.id)}
                >
                  {lesson.plans?.map(plan => (
                    <TreeNode
                      key={`p-${plan.id}`}
                      level={3}
                      label={
                        <span className="text-gray-500 font-mono text-xs">
                          Plan V{plan.version_number} {plan.is_final ? '(Final)' : ''}
                        </span>
                      }
                      icon={FileText}
                      defaultExpanded={true}
                      // 🔥 新增: Plan 层级的操作按钮
                      actions={renderActions('plan', plan.id)}
                    >
                      {sortSlides(plan.slides).map(slide => (
                        <TreeNode
                          key={`sl-${slide.id}`}
                          level={4}
                          label={
                            <span className="text-sm">
                              P{slide.slide_index}: {slide.title} 
                              <span className="text-xs text-gray-400 ml-1">(v{slide.version_number})</span>
                            </span>
                          }
                          icon={FileText}
                          // 🔥 修正: 传递默认版本号用于 Prompt
                          actions={renderActions('slide', slide.id, { defaultVersion: slide.version_number })}
                        />
                      ))}
                    </TreeNode>
                  ))}
                </TreeNode>
              ))}
            </TreeNode>
          ))}
        </TreeNode>
      ))}
    </>
  );
};
